import React, { useEffect, useState } from 'react';
import { QuoteData } from '../../App';
import { UsersIcon, BuildingIcon, UserIcon, CheckCircleIcon, ChevronDownIcon, MapPinIcon, PhoneIcon, MailIcon, PercentIcon, PlusCircleIcon, TrashIcon, TagIcon } from 'lucide-react';
interface ClientProps {
  quoteData: QuoteData;
  setQuoteData: (data: QuoteData) => void;
}
type QuoteType = 'dealer' | 'dealer-customer' | 'final-customer';
interface Dealer {
  id: string;
  name: string;
  code: string;
  address: string;
  city: string;
  country: string;
  phone: string;
  email: string;
  taxId: string;
}
interface Customer {
  id: string;
  name: string;
  dealerId: string;
  address: string;
  city: string;
  country: string;
  phone: string;
  email: string;
  taxId: string;
}
interface Discount {
  id: string;
  name: string;
  percentage: number;
}
export function Client({
  quoteData,
  setQuoteData
}: ClientProps) {
  const [quoteType, setQuoteType] = useState<QuoteType>(quoteData.quoteType || 'dealer');
  const [selectedDealer, setSelectedDealer] = useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [isCustomerFromDealer, setIsCustomerFromDealer] = useState(true);
  // Dealer discounts
  const [dealerBaseDiscount, setDealerBaseDiscount] = useState(10);
  const [dealerAdditionalDiscounts, setDealerAdditionalDiscounts] = useState<Discount[]>([]);
  // Dealer margin (NEW)
  const [dealerMargin, setDealerMargin] = useState(15);
  // Customer discounts
  const [customerAdditionalDiscounts, setCustomerAdditionalDiscounts] = useState<Discount[]>([]);
  // Mock data - En producción vendría de una API
  const dealers: Dealer[] = [{
    id: 'D001',
    name: 'North Region Distributors',
    code: 'NRD-001',
    address: '123 Industrial Ave',
    city: 'Chicago',
    country: 'USA',
    phone: '+1 (555) 123-4567',
    email: 'contact@northregion.com',
    taxId: 'TAX-NRD-001'
  }, {
    id: 'D002',
    name: 'South Valley Equipment',
    code: 'SVE-002',
    address: '456 Commerce Blvd',
    city: 'Houston',
    country: 'USA',
    phone: '+1 (555) 234-5678',
    email: 'info@southvalley.com',
    taxId: 'TAX-SVE-002'
  }, {
    id: 'D003',
    name: 'East Coast Agricultural',
    code: 'ECA-003',
    address: '789 Trade Street',
    city: 'Atlanta',
    country: 'USA',
    phone: '+1 (555) 345-6789',
    email: 'sales@eastcoastagri.com',
    taxId: 'TAX-ECA-003'
  }];
  const customers: Customer[] = [{
    id: 'C001',
    name: 'ABC Farm Corporation',
    dealerId: 'D001',
    address: '100 Farm Road',
    city: 'Springfield',
    country: 'USA',
    phone: '+1 (555) 111-2222',
    email: 'john@abcfarm.com',
    taxId: 'TAX-ABC-001'
  }, {
    id: 'C002',
    name: 'XYZ Agriculture Ltd',
    dealerId: 'D001',
    address: '200 Harvest Lane',
    city: 'Madison',
    country: 'USA',
    phone: '+1 (555) 222-3333',
    email: 'mary@xyzagri.com',
    taxId: 'TAX-XYZ-002'
  }, {
    id: 'C003',
    name: 'Green Valley Farms',
    dealerId: 'D002',
    address: '300 Valley Drive',
    city: 'Austin',
    country: 'USA',
    phone: '+1 (555) 333-4444',
    email: 'robert@greenvalley.com',
    taxId: 'TAX-GVF-003'
  }, {
    id: 'C004',
    name: 'Sunrise Agricultural Co',
    dealerId: 'D002',
    address: '400 Sunrise Blvd',
    city: 'Dallas',
    country: 'USA',
    phone: '+1 (555) 444-5555',
    email: 'info@sunriseagri.com',
    taxId: 'TAX-SAC-004'
  }, {
    id: 'C005',
    name: 'Golden Harvest Enterprises',
    dealerId: 'D003',
    address: '500 Golden Road',
    city: 'Savannah',
    country: 'USA',
    phone: '+1 (555) 555-6666',
    email: 'contact@goldenharvest.com',
    taxId: 'TAX-GHE-005'
  }, {
    id: 'C006',
    name: 'Direct Customer Inc',
    dealerId: '',
    address: '600 Direct Street',
    city: 'Phoenix',
    country: 'USA',
    phone: '+1 (555) 666-7777',
    email: 'info@directcustomer.com',
    taxId: 'TAX-DCI-006'
  }];
  const getSelectedDealer = () => {
    return dealers.find(d => d.id === selectedDealer);
  };
  const getSelectedCustomer = () => {
    return customers.find(c => c.id === selectedCustomer);
  };
  const getCustomersForDealer = (dealerId: string) => {
    return customers.filter(c => c.dealerId === dealerId);
  };
  const getAllCustomers = () => {
    return customers;
  };
  const BillingInfoCard = ({
    title,
    entity,
    icon: Icon
  }: {
    title: string;
    entity: Dealer | Customer | undefined;
    icon: any;
  }) => {
    if (!entity) return null;
    return <div className="bg-white rounded-xl border-2 border-gray-200 p-6" data-id="element-554">
        <div className="flex items-start gap-4 mb-4" data-id="element-555">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-556">
            <Icon className="w-6 h-6 text-blue-600" data-id="element-557" />
          </div>
          <div className="flex-1" data-id="element-558">
            <h4 className="text-lg font-bold text-gray-900 mb-1" data-id="element-559">{title}</h4>
            <p className="text-sm text-gray-500" data-id="element-560">Billing Information</p>
          </div>
        </div>
        <div className="space-y-3" data-id="element-561">
          <div data-id="element-562">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-563">
              Company Name
            </p>
            <p className="text-base font-semibold text-gray-900" data-id="element-564">
              {entity.name}
            </p>
          </div>
          {'code' in entity && <div data-id="element-565">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-566">
                Dealer Code
              </p>
              <p className="text-sm text-gray-900" data-id="element-567">{entity.code}</p>
            </div>}
          <div data-id="element-568">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-569">
              Tax ID
            </p>
            <p className="text-sm text-gray-900" data-id="element-570">{entity.taxId}</p>
          </div>
          <div className="pt-3 border-t border-gray-200" data-id="element-571">
            <div className="space-y-2" data-id="element-572">
              <div className="flex items-start gap-2" data-id="element-573">
                <MapPinIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-574" />
                <div className="text-sm text-gray-700" data-id="element-575">
                  <p data-id="element-576">{entity.address}</p>
                  <p data-id="element-577">
                    {entity.city}, {entity.country}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2" data-id="element-578">
                <PhoneIcon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-579" />
                <p className="text-sm text-gray-700" data-id="element-580">{entity.phone}</p>
              </div>
              <div className="flex items-center gap-2" data-id="element-581">
                <MailIcon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-582" />
                <p className="text-sm text-gray-700" data-id="element-583">{entity.email}</p>
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  const handleAddDealerDiscount = () => {
    const newDiscount: Discount = {
      id: Date.now().toString(),
      name: '',
      percentage: 0
    };
    setDealerAdditionalDiscounts([...dealerAdditionalDiscounts, newDiscount]);
  };
  const handleRemoveDealerDiscount = (id: string) => {
    setDealerAdditionalDiscounts(dealerAdditionalDiscounts.filter(d => d.id !== id));
  };
  const handleDealerDiscountChange = (id: string, field: 'name' | 'percentage', value: string | number) => {
    setDealerAdditionalDiscounts(dealerAdditionalDiscounts.map(d => d.id === id ? {
      ...d,
      [field]: value
    } : d));
  };
  const handleAddCustomerDiscount = () => {
    const newDiscount: Discount = {
      id: Date.now().toString(),
      name: '',
      percentage: 0
    };
    setCustomerAdditionalDiscounts([...customerAdditionalDiscounts, newDiscount]);
  };
  const handleRemoveCustomerDiscount = (id: string) => {
    setCustomerAdditionalDiscounts(customerAdditionalDiscounts.filter(d => d.id !== id));
  };
  const handleCustomerDiscountChange = (id: string, field: 'name' | 'percentage', value: string | number) => {
    setCustomerAdditionalDiscounts(customerAdditionalDiscounts.map(d => d.id === id ? {
      ...d,
      [field]: value
    } : d));
  };
  const getDealerTotalDiscount = () => {
    const additional = dealerAdditionalDiscounts.reduce((sum, d) => sum + d.percentage, 0);
    return dealerBaseDiscount + additional;
  };
  const getCustomerTotalDiscount = () => {
    return customerAdditionalDiscounts.reduce((sum, d) => sum + d.percentage, 0);
  };
  const DiscountSection = ({
    title,
    baseDiscount,
    setBaseDiscount,
    additionalDiscounts,
    onAddDiscount,
    onRemoveDiscount,
    onDiscountChange,
    showBaseDiscount,
    icon: Icon,
    color
  }: {
    title: string;
    baseDiscount?: number;
    setBaseDiscount?: (value: number) => void;
    additionalDiscounts: Discount[];
    onAddDiscount: () => void;
    onRemoveDiscount: (id: string) => void;
    onDiscountChange: (id: string, field: 'name' | 'percentage', value: string | number) => void;
    showBaseDiscount: boolean;
    icon: any;
    color: string;
  }) => {
    const totalDiscount = showBaseDiscount ? (baseDiscount || 0) + additionalDiscounts.reduce((sum, d) => sum + d.percentage, 0) : additionalDiscounts.reduce((sum, d) => sum + d.percentage, 0);
    return <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden" data-id="element-584">
        <div className={`bg-gradient-to-r ${color} p-4 border-b border-gray-200`} data-id="element-585">
          <div className="flex items-center justify-between" data-id="element-586">
            <div className="flex items-center gap-3" data-id="element-587">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center" data-id="element-588">
                <Icon className="w-5 h-5 text-gray-700" data-id="element-589" />
              </div>
              <div data-id="element-590">
                <h4 className="text-base font-bold text-gray-900" data-id="element-591">{title}</h4>
                <p className="text-sm text-gray-600" data-id="element-592">
                  Configure discount structure
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-2 shadow-sm" data-id="element-593">
              <PercentIcon className="w-5 h-5 text-gray-700" data-id="element-594" />
              <span className="text-lg font-bold text-gray-900" data-id="element-595">
                {totalDiscount.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6" data-id="element-596">
          {/* Base Discount */}
          {showBaseDiscount && <div data-id="element-597">
              <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-598">
                Base Discount <span className="text-red-500" data-id="element-599">*</span>
              </label>
              <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200" data-id="element-600">
                <div className="flex items-center gap-4" data-id="element-601">
                  <div className="flex-1" data-id="element-602">
                    <input type="range" min="0" max="50" step="0.5" value={baseDiscount} onChange={e => setBaseDiscount && setBaseDiscount(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb" style={{
                  background: `linear-gradient(to right, rgb(59, 130, 246) 0%, rgb(59, 130, 246) ${(baseDiscount || 0) / 50 * 100}%, rgb(229, 231, 235) ${(baseDiscount || 0) / 50 * 100}%, rgb(229, 231, 235) 100%)`
                }} data-id="element-603" />
                  </div>
                  <div className="flex items-center gap-2" data-id="element-604">
                    <input type="number" min="0" max="50" step="0.5" value={baseDiscount} onChange={e => setBaseDiscount && setBaseDiscount(Number(e.target.value))} className="w-20 px-3 py-2 bg-white border-2 border-gray-300 rounded-lg text-center font-bold focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-605" />
                    <span className="text-gray-600 font-semibold" data-id="element-606">%</span>
                  </div>
                </div>
              </div>
            </div>}
          {/* Additional Discounts */}
          <div data-id="element-607">
            <div className="flex items-center justify-between mb-3" data-id="element-608">
              <label className="text-sm font-semibold text-gray-900" data-id="element-609">
                Additional Discounts
              </label>
              <button onClick={onAddDiscount} className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow-md" data-id="element-610">
                <PlusCircleIcon className="w-4 h-4" data-id="element-611" />
                Add Discount
              </button>
            </div>
            {additionalDiscounts.length === 0 ? <div className="bg-gray-50 rounded-lg p-6 text-center border-2 border-dashed border-gray-300" data-id="element-612">
                <TagIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" data-id="element-613" />
                <p className="text-sm text-gray-500" data-id="element-614">
                  No additional discounts added yet
                </p>
                <p className="text-xs text-gray-400 mt-1" data-id="element-615">
                  Click "Add Discount" to create a new discount
                </p>
              </div> : <div className="space-y-3" data-id="element-616">
                {additionalDiscounts.map((discount, index) => <div key={discount.id} className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200" data-id="element-617">
                    <div className="flex items-center gap-3" data-id="element-618">
                      <div className="flex-1 grid grid-cols-2 gap-3" data-id="element-619">
                        <div data-id="element-620">
                          <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-621">
                            Discount Name
                          </label>
                          <input type="text" value={discount.name} onChange={e => onDiscountChange(discount.id, 'name', e.target.value)} placeholder="e.g., Volume discount" className="w-full px-3 py-2 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-622" />
                        </div>
                        <div data-id="element-623">
                          <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-624">
                            Percentage
                          </label>
                          <div className="flex items-center gap-2" data-id="element-625">
                            <input type="number" min="0" max="50" step="0.5" value={discount.percentage} onChange={e => onDiscountChange(discount.id, 'percentage', Number(e.target.value))} className="w-full px-3 py-2 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-semibold" data-id="element-626" />
                            <span className="text-gray-600 font-semibold" data-id="element-627">
                              %
                            </span>
                          </div>
                        </div>
                      </div>
                      <button onClick={() => onRemoveDiscount(discount.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Remove discount" data-id="element-628">
                        <TrashIcon className="w-5 h-5" data-id="element-629" />
                      </button>
                    </div>
                  </div>)}
              </div>}
          </div>
          {/* Discount Summary */}
          {(showBaseDiscount || additionalDiscounts.length > 0) && <div className="bg-blue-50 rounded-lg p-4 border border-blue-200" data-id="element-630">
              <div className="flex items-center justify-between" data-id="element-631">
                <div data-id="element-632">
                  <p className="text-sm font-semibold text-blue-900" data-id="element-633">
                    Total Discount Applied
                  </p>
                  <p className="text-xs text-blue-700 mt-1" data-id="element-634">
                    {showBaseDiscount && `Base: ${baseDiscount}%`}
                    {showBaseDiscount && additionalDiscounts.length > 0 && ' + '}
                    {additionalDiscounts.length > 0 && `${additionalDiscounts.length} additional discount${additionalDiscounts.length > 1 ? 's' : ''}`}
                  </p>
                </div>
                <div className="text-right" data-id="element-635">
                  <p className="text-2xl font-bold text-blue-900" data-id="element-636">
                    {totalDiscount.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>}
        </div>
      </div>;
  };
  // Update quoteData when quoteType changes
  useEffect(() => {
    setQuoteData({
      ...quoteData,
      quoteType: quoteType
    });
  }, [quoteType]);
  return <div className="w-full space-y-6" data-id="element-637">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-638">
        <div className="bg-gray-50 p-5 border-b border-gray-200" data-id="element-639">
          <div className="flex items-center gap-3" data-id="element-640">
            <div className="w-10 h-10 bg-gray-400 rounded-lg flex items-center justify-center" data-id="element-641">
              <UsersIcon className="w-5 h-5 text-white" data-id="element-642" />
            </div>
            <div data-id="element-643">
              <h3 className="text-base font-semibold text-gray-900" data-id="element-644">
                Client Selection
              </h3>
              <p className="text-sm text-gray-500" data-id="element-645">
                Select the type of quote and client information
              </p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-5" data-id="element-646">
          {/* Quote Type Selection - REDESIGNED */}
          <div data-id="element-647">
            <label className="block text-sm font-bold text-gray-900 mb-4" data-id="element-648">
              Select Quote Type <span className="text-red-500" data-id="element-649">*</span>
            </label>
            <div className="grid grid-cols-3 gap-4" data-id="element-650">
              {/* Quote for Dealer */}
              <button onClick={() => {
              setQuoteType('dealer');
              setSelectedCustomer('');
            }} className={`group relative overflow-hidden rounded-xl transition-all duration-300 ${quoteType === 'dealer' ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-600 shadow-lg shadow-blue-200/50 scale-102' : 'bg-white border-2 border-gray-200 hover:border-blue-300 hover:shadow-md hover:scale-101'}`} data-id="element-651">
                {/* Selection indicator */}
                {quoteType === 'dealer' && <div className="absolute top-3 right-3 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center shadow-md" data-id="element-652">
                    <CheckCircleIcon className="w-4 h-4 text-white" data-id="element-653" />
                  </div>}
                {/* Background decoration */}
                <div className={`absolute inset-0 bg-gradient-to-br transition-opacity duration-300 ${quoteType === 'dealer' ? 'from-blue-400/10 to-blue-600/10 opacity-100' : 'from-gray-50 to-gray-100 opacity-0 group-hover:opacity-100'}`} data-id="element-654"></div>
                <div className="relative p-5 flex flex-col items-center text-center space-y-3" data-id="element-655">
                  {/* Icon container */}
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 ${quoteType === 'dealer' ? 'bg-blue-600 shadow-md shadow-blue-600/30' : 'bg-gray-100 group-hover:bg-blue-100'}`} data-id="element-656">
                    <BuildingIcon className={`w-7 h-7 transition-colors duration-300 ${quoteType === 'dealer' ? 'text-white' : 'text-gray-500 group-hover:text-blue-600'}`} data-id="element-657" />
                  </div>
                  {/* Title */}
                  <div data-id="element-658">
                    <h4 className={`text-base font-bold mb-1 transition-colors duration-300 ${quoteType === 'dealer' ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-659">
                      Quote for Dealer
                    </h4>
                    <p className={`text-xs leading-relaxed transition-colors duration-300 ${quoteType === 'dealer' ? 'text-blue-700' : 'text-gray-600'}`} data-id="element-660">
                      Generate a quote directly for a dealer with their billing
                      information
                    </p>
                  </div>
                </div>
              </button>
              {/* Dealer & Customer */}
              <button onClick={() => {
              setQuoteType('dealer-customer');
            }} className={`group relative overflow-hidden rounded-xl transition-all duration-300 ${quoteType === 'dealer-customer' ? 'bg-gradient-to-br from-indigo-50 to-indigo-100 border-2 border-indigo-600 shadow-lg shadow-indigo-200/50 scale-102' : 'bg-white border-2 border-gray-200 hover:border-indigo-300 hover:shadow-md hover:scale-101'}`} data-id="element-661">
                {/* Selection indicator */}
                {quoteType === 'dealer-customer' && <div className="absolute top-3 right-3 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center shadow-md" data-id="element-662">
                    <CheckCircleIcon className="w-4 h-4 text-white" data-id="element-663" />
                  </div>}
                {/* Background decoration */}
                <div className={`absolute inset-0 bg-gradient-to-br transition-opacity duration-300 ${quoteType === 'dealer-customer' ? 'from-indigo-400/10 to-indigo-600/10 opacity-100' : 'from-gray-50 to-gray-100 opacity-0 group-hover:opacity-100'}`} data-id="element-664"></div>
                <div className="relative p-5 flex flex-col items-center text-center space-y-3" data-id="element-665">
                  {/* Icon container */}
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 ${quoteType === 'dealer-customer' ? 'bg-indigo-600 shadow-md shadow-indigo-600/30' : 'bg-gray-100 group-hover:bg-indigo-100'}`} data-id="element-666">
                    <div className="flex items-center gap-0.5" data-id="element-667">
                      <BuildingIcon className={`w-6 h-6 transition-colors duration-300 ${quoteType === 'dealer-customer' ? 'text-white' : 'text-gray-500 group-hover:text-indigo-600'}`} data-id="element-668" />
                      <UsersIcon className={`w-6 h-6 transition-colors duration-300 ${quoteType === 'dealer-customer' ? 'text-white' : 'text-gray-500 group-hover:text-indigo-600'}`} data-id="element-669" />
                    </div>
                  </div>
                  {/* Title */}
                  <div data-id="element-670">
                    <h4 className={`text-base font-bold mb-1 transition-colors duration-300 ${quoteType === 'dealer-customer' ? 'text-indigo-900' : 'text-gray-900'}`} data-id="element-671">
                      Dealer & Customer
                    </h4>
                    <p className={`text-xs leading-relaxed transition-colors duration-300 ${quoteType === 'dealer-customer' ? 'text-indigo-700' : 'text-gray-600'}`} data-id="element-672">
                      Create two quotes: one for the dealer and one for the
                      final customer
                    </p>
                  </div>
                </div>
              </button>
              {/* Final Customer */}
              <button onClick={() => {
              setQuoteType('final-customer');
              setSelectedDealer('');
            }} className={`group relative overflow-hidden rounded-xl transition-all duration-300 ${quoteType === 'final-customer' ? 'bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-600 shadow-lg shadow-green-200/50 scale-102' : 'bg-white border-2 border-gray-200 hover:border-green-300 hover:shadow-md hover:scale-101'}`} data-id="element-673">
                {/* Selection indicator */}
                {quoteType === 'final-customer' && <div className="absolute top-3 right-3 w-6 h-6 bg-green-600 rounded-full flex items-center justify-center shadow-md" data-id="element-674">
                    <CheckCircleIcon className="w-4 h-4 text-white" data-id="element-675" />
                  </div>}
                {/* Background decoration */}
                <div className={`absolute inset-0 bg-gradient-to-br transition-opacity duration-300 ${quoteType === 'final-customer' ? 'from-green-400/10 to-green-600/10 opacity-100' : 'from-gray-50 to-gray-100 opacity-0 group-hover:opacity-100'}`} data-id="element-676"></div>
                <div className="relative p-5 flex flex-col items-center text-center space-y-3" data-id="element-677">
                  {/* Icon container */}
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 ${quoteType === 'final-customer' ? 'bg-green-600 shadow-md shadow-green-600/30' : 'bg-gray-100 group-hover:bg-green-100'}`} data-id="element-678">
                    <UserIcon className={`w-7 h-7 transition-colors duration-300 ${quoteType === 'final-customer' ? 'text-white' : 'text-gray-500 group-hover:text-green-600'}`} data-id="element-679" />
                  </div>
                  {/* Title */}
                  <div data-id="element-680">
                    <h4 className={`text-base font-bold mb-1 transition-colors duration-300 ${quoteType === 'final-customer' ? 'text-green-900' : 'text-gray-900'}`} data-id="element-681">
                      Final Customer
                    </h4>
                    <p className={`text-xs leading-relaxed transition-colors duration-300 ${quoteType === 'final-customer' ? 'text-green-700' : 'text-gray-600'}`} data-id="element-682">
                      Quote directly to a final customer, with optional dealer
                      association
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Quote for Dealer */}
      {quoteType === 'dealer' && <div className="space-y-6" data-id="element-683">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-684">
            <div className="bg-gray-50 p-5 border-b border-gray-200" data-id="element-685">
              <div className="flex items-center gap-3" data-id="element-686">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-687">
                  <BuildingIcon className="w-5 h-5 text-blue-600" data-id="element-688" />
                </div>
                <div data-id="element-689">
                  <h3 className="text-base font-semibold text-gray-900" data-id="element-690">
                    Dealer Information
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-691">
                    Select the dealer for this quote
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-6" data-id="element-692">
              <div data-id="element-693">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-694">
                  Select Dealer <span className="text-red-500" data-id="element-695">*</span>
                </label>
                <div className="relative" data-id="element-696">
                  <select value={selectedDealer} onChange={e => setSelectedDealer(e.target.value)} className="w-full px-4 py-3 pr-10 bg-white border-2 border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" data-id="element-697">
                    <option value="" data-id="element-698">Choose a dealer...</option>
                    {dealers.map(dealer => <option key={dealer.id} value={dealer.id} data-id="element-699">
                        {dealer.name} ({dealer.code})
                      </option>)}
                  </select>
                  <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-700" />
                </div>
              </div>
              {selectedDealer && <BillingInfoCard title="Dealer" entity={getSelectedDealer()} icon={BuildingIcon} data-id="element-701" />}
            </div>
          </div>
          {/* Dealer Discounts */}
          {selectedDealer && <DiscountSection title="Dealer Discounts" baseDiscount={dealerBaseDiscount} setBaseDiscount={setDealerBaseDiscount} additionalDiscounts={dealerAdditionalDiscounts} onAddDiscount={handleAddDealerDiscount} onRemoveDiscount={handleRemoveDealerDiscount} onDiscountChange={handleDealerDiscountChange} showBaseDiscount={true} icon={PercentIcon} color="from-blue-50 to-blue-100" data-id="element-702" />}
        </div>}
      {/* Quote for Dealer and Customer */}
      {quoteType === 'dealer-customer' && <div className="space-y-6" data-id="element-703">
          {/* Dealer Selection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-704">
            <div className="bg-gray-50 p-5 border-b border-gray-200" data-id="element-705">
              <div className="flex items-center gap-3" data-id="element-706">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-707">
                  <BuildingIcon className="w-5 h-5 text-blue-600" data-id="element-708" />
                </div>
                <div data-id="element-709">
                  <h3 className="text-base font-semibold text-gray-900" data-id="element-710">
                    Dealer Information
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-711">
                    Select the dealer for this quote
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-6" data-id="element-712">
              <div data-id="element-713">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-714">
                  Select Dealer <span className="text-red-500" data-id="element-715">*</span>
                </label>
                <div className="relative" data-id="element-716">
                  <select value={selectedDealer} onChange={e => {
                setSelectedDealer(e.target.value);
                setSelectedCustomer('');
              }} className="w-full px-4 py-3 pr-10 bg-white border-2 border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" data-id="element-717">
                    <option value="" data-id="element-718">Choose a dealer...</option>
                    {dealers.map(dealer => <option key={dealer.id} value={dealer.id} data-id="element-719">
                        {dealer.name} ({dealer.code})
                      </option>)}
                  </select>
                  <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-720" />
                </div>
              </div>
              {selectedDealer && <BillingInfoCard title="Dealer" entity={getSelectedDealer()} icon={BuildingIcon} data-id="element-721" />}
            </div>
          </div>
          {/* Customer Selection */}
          {selectedDealer && <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-722">
              <div className="bg-gray-50 p-5 border-b border-gray-200" data-id="element-723">
                <div className="flex items-center gap-3" data-id="element-724">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-725">
                    <UserIcon className="w-5 h-5 text-green-600" data-id="element-726" />
                  </div>
                  <div data-id="element-727">
                    <h3 className="text-base font-semibold text-gray-900" data-id="element-728">
                      Customer Information
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-729">
                      Select the customer from this dealer
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-6" data-id="element-730">
                <div data-id="element-731">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-732">
                    Select Customer <span className="text-red-500" data-id="element-733">*</span>
                  </label>
                  <div className="relative" data-id="element-734">
                    <select value={selectedCustomer} onChange={e => setSelectedCustomer(e.target.value)} className="w-full px-4 py-3 pr-10 bg-white border-2 border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" data-id="element-735">
                      <option value="" data-id="element-736">Choose a customer...</option>
                      {getCustomersForDealer(selectedDealer).map(customer => <option key={customer.id} value={customer.id} data-id="element-737">
                          {customer.name}
                        </option>)}
                    </select>
                    <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-738" />
                  </div>
                </div>
                {selectedCustomer && <BillingInfoCard title="Customer" entity={getSelectedCustomer()} icon={UserIcon} data-id="element-739" />}
              </div>
            </div>}
          {/* Dealer Discounts */}
          {selectedDealer && <DiscountSection title="Dealer Discounts" baseDiscount={dealerBaseDiscount} setBaseDiscount={setDealerBaseDiscount} additionalDiscounts={dealerAdditionalDiscounts} onAddDiscount={handleAddDealerDiscount} onRemoveDiscount={handleRemoveDealerDiscount} onDiscountChange={handleDealerDiscountChange} showBaseDiscount={true} icon={PercentIcon} color="from-blue-50 to-blue-100" data-id="element-740" />}
          {/* Dealer Margin Configuration (NEW) */}
          {selectedDealer && <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden" data-id="element-741">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 border-b border-gray-200" data-id="element-742">
                <div className="flex items-center justify-between" data-id="element-743">
                  <div className="flex items-center gap-3" data-id="element-744">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center" data-id="element-745">
                      <PercentIcon className="w-5 h-5 text-indigo-700" data-id="element-746" />
                    </div>
                    <div data-id="element-747">
                      <h4 className="text-base font-bold text-gray-900" data-id="element-748">
                        Dealer Margin
                      </h4>
                      <p className="text-sm text-gray-600" data-id="element-749">
                        Configure the profit margin for the dealer
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-2 shadow-sm" data-id="element-750">
                    
                    <span className="text-lg font-bold text-gray-900" data-id="element-751">
                      {dealerMargin.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-6" data-id="element-752">
                <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200" data-id="element-753">
                  <div className="flex items-center gap-4" data-id="element-754">
                    <div className="flex-1" data-id="element-755">
                      <input type="range" min="0" max="100" step="0.5" value={dealerMargin} onChange={e => setDealerMargin(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb" style={{
                  background: `linear-gradient(to right, rgb(99, 102, 241) 0%, rgb(99, 102, 241) ${dealerMargin}%, rgb(229, 231, 235) ${dealerMargin}%, rgb(229, 231, 235) 100%)`
                }} data-id="element-756" />
                    </div>
                    <div className="flex items-center gap-2" data-id="element-757">
                      <input type="number" min="0" max="100" step="0.5" value={dealerMargin} onChange={e => setDealerMargin(Number(e.target.value))} className="w-20 px-3 py-2 bg-white border-2 border-gray-300 rounded-lg text-center font-bold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-758" />
                      <span className="text-gray-600 font-semibold" data-id="element-759">%</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 bg-indigo-50 rounded-lg p-4 border border-indigo-200" data-id="element-760">
                  <p className="text-sm text-indigo-900 mb-2" data-id="element-761">
                    <span className="font-semibold" data-id="element-762">💡 How it works:</span>
                  </p>
                  <p className="text-xs text-indigo-700" data-id="element-763">
                    The dealer margin is the percentage added on top of your
                    cost price to calculate the price shown to the customer.
                    This represents the dealer's profit.
                  </p>
                </div>
              </div>
            </div>}
          {/* Customer Discounts */}
          {selectedCustomer && <DiscountSection title="Customer Discounts" additionalDiscounts={customerAdditionalDiscounts} onAddDiscount={handleAddCustomerDiscount} onRemoveDiscount={handleRemoveCustomerDiscount} onDiscountChange={handleCustomerDiscountChange} showBaseDiscount={false} icon={TagIcon} color="from-green-50 to-green-100" data-id="element-764" />}
          {/* Summary for Both */}
          {selectedDealer && selectedCustomer && <div className="bg-blue-50 rounded-xl border border-blue-200 p-6" data-id="element-765">
              <div className="flex items-start gap-3 mb-4" data-id="element-766">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-767">
                  <CheckCircleIcon className="w-5 h-5 text-white" data-id="element-768" />
                </div>
                <div data-id="element-769">
                  <h4 className="text-base font-bold text-blue-900 mb-1" data-id="element-770">
                    Two Quotes Will Be Generated
                  </h4>
                  <p className="text-sm text-blue-700" data-id="element-771">
                    One quote for the dealer and one for the final customer
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-772">
                <div className="bg-white rounded-lg p-4" data-id="element-773">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-774">
                    Dealer Quote
                  </p>
                  <p className="text-sm font-bold text-gray-900" data-id="element-775">
                    {getSelectedDealer()?.name}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4" data-id="element-776">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-777">
                    Customer Quote
                  </p>
                  <p className="text-sm font-bold text-gray-900" data-id="element-778">
                    {getSelectedCustomer()?.name}
                  </p>
                </div>
              </div>
            </div>}
        </div>}
      {/* Quote for Final Customer */}
      {quoteType === 'final-customer' && <div className="space-y-6" data-id="element-779">
          {/* Customer Type Toggle */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-780">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-781">
              <input type="checkbox" checked={isCustomerFromDealer} onChange={e => {
            setIsCustomerFromDealer(e.target.checked);
            setSelectedDealer('');
            setSelectedCustomer('');
          }} className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500" data-id="element-782" />
              <div data-id="element-783">
                <p className="text-sm font-semibold text-gray-900" data-id="element-784">
                  Customer belongs to a dealer
                </p>
                <p className="text-xs text-gray-500" data-id="element-785">
                  Check this if the customer is part of a dealer's network
                </p>
              </div>
            </label>
          </div>
          {/* Dealer Selection (if customer is from dealer) */}
          {isCustomerFromDealer && <div data-id="element-786">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-787">
                Select Dealer <span className="text-red-500" data-id="element-788">*</span>
              </label>
              <div className="relative" data-id="element-789">
                <select value={selectedDealer} onChange={e => {
            setSelectedDealer(e.target.value);
            setSelectedCustomer('');
          }} className="w-full px-4 py-3 pr-10 bg-white border-2 border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" data-id="element-790">
                  <option value="" data-id="element-791">Choose a dealer...</option>
                  {dealers.map(dealer => <option key={dealer.id} value={dealer.id} data-id="element-792">
                      {dealer.name} ({dealer.code})
                    </option>)}
                </select>
                <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-793" />
              </div>
            </div>}
          {/* Customer Selection */}
          <div data-id="element-794">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-795">
              Select Customer <span className="text-red-500" data-id="element-796">*</span>
            </label>
            <div className="relative" data-id="element-797">
              <select value={selectedCustomer} onChange={e => setSelectedCustomer(e.target.value)} className="w-full px-4 py-3 pr-10 bg-white border-2 border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" disabled={isCustomerFromDealer && !selectedDealer} data-id="element-798">
                <option value="" data-id="element-799">
                  {isCustomerFromDealer && !selectedDealer ? 'Select a dealer first...' : 'Choose a customer...'}
                </option>
                {(isCustomerFromDealer && selectedDealer ? getCustomersForDealer(selectedDealer) : getAllCustomers().filter(c => !c.dealerId)).map(customer => <option key={customer.id} value={customer.id} data-id="element-800">
                    {customer.name}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-801" />
            </div>
          </div>
          {selectedCustomer && <BillingInfoCard title="Final Customer" entity={getSelectedCustomer()} icon={UserIcon} data-id="element-802" />}
          {isCustomerFromDealer && selectedDealer && selectedCustomer && <div className="bg-blue-50 rounded-lg p-4 border border-blue-200" data-id="element-803">
              <div className="flex items-start gap-3" data-id="element-804">
                <BuildingIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" data-id="element-805" />
                <div data-id="element-806">
                  <p className="text-sm font-semibold text-blue-900 mb-1" data-id="element-807">
                    Associated Dealer
                  </p>
                  <p className="text-sm text-blue-700" data-id="element-808">
                    {getSelectedDealer()?.name}
                  </p>
                  <p className="text-xs text-blue-600 mt-1" data-id="element-809">
                    This customer is part of the dealer's network
                  </p>
                </div>
              </div>
            </div>}
          {/* Customer Discounts */}
          {selectedCustomer && <DiscountSection title="Customer Discounts" additionalDiscounts={customerAdditionalDiscounts} onAddDiscount={handleAddCustomerDiscount} onRemoveDiscount={handleRemoveCustomerDiscount} onDiscountChange={handleCustomerDiscountChange} showBaseDiscount={false} icon={TagIcon} color="from-green-50 to-green-100" data-id="element-810" />}
        </div>}
      {/* Summary Card */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-6" data-id="element-811">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3" data-id="element-812">
          Selection Summary
        </h3>
        <div className="space-y-2 text-sm" data-id="element-813">
          <div className="flex justify-between" data-id="element-814">
            <span className="text-gray-600" data-id="element-815">Quote Type:</span>
            <span className="font-semibold text-gray-900" data-id="element-816">
              {quoteType === 'dealer' && 'Quote for Dealer'}
              {quoteType === 'dealer-customer' && 'Dealer & Customer'}
              {quoteType === 'final-customer' && 'Final Customer'}
            </span>
          </div>
          {selectedDealer && <div className="flex justify-between" data-id="element-817">
              <span className="text-gray-600" data-id="element-818">Dealer:</span>
              <span className="font-semibold text-gray-900" data-id="element-819">
                {getSelectedDealer()?.name}
              </span>
            </div>}
          {selectedCustomer && <div className="flex justify-between" data-id="element-820">
              <span className="text-gray-600" data-id="element-821">Customer:</span>
              <span className="font-semibold text-gray-900" data-id="element-822">
                {getSelectedCustomer()?.name}
              </span>
            </div>}
        </div>
      </div>
    </div>;
}